import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  AlertTriangle,
  ArrowLeft,
  Brain,
  CalendarClock,
  CheckCircle,
  ExternalLink,
  FlaskConical,
  Info,
  Loader2,
  MessageCircle,
  Phone,
  RefreshCw,
  Save,
  Search,
  Send,
  Armchair,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import ConversationComposer from "@/components/ConversationComposer";

/**
 * SUPERADMIN → IA → "Probar a Carmen" (sep-2026).
 *
 * Simulador de conversación EN SECO con el cerebro de Carmen de un negocio
 * concreto: elige negocio, canal (voz/WhatsApp) y quién eres (cliente
 * conocido o desconocido) y chatea por texto como si fueras el cliente.
 * Ninguna acción de las herramientas se ejecuta de verdad — cada respuesta
 * enseña qué herramienta llamó Carmen, con qué datos y si fue "real"
 * (consulta) o "en seco" (acción simulada).
 *
 * Contrato de backend en `/admin/carmen-sim/*` (construido en paralelo por
 * el agente backend, dado por bueno tal cual: `{ success, data }` /
 * `{ success:false, message }`, 503 + code "CARMEN_SIM_DISABLED" si el
 * simulador está apagado).
 */

type Channel = "voice" | "whatsapp";
type CallerKind = "known" | "unknown";
type ToolCallMode = "real" | "seco";
type BookingMode = "tables" | "appointments";

interface BusinessItem {
  locationId: string;
  locationName: string;
  organizationName: string;
  bookingMode: BookingMode;
  channels: { voice: boolean; whatsapp: boolean };
}

interface CustomerItem {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
}

interface ToolCall {
  name: string;
  args: Record<string, unknown>;
  mode: ToolCallMode;
  result: string;
}

interface SessionStartResponse {
  sessionId: string;
  promptChars: number;
  tokensAprox: number;
  herramientas: string[];
  saludo: string | null;
}

interface MessageResponse {
  reply: string;
  toolCalls: ToolCall[];
  usage: { promptTokens: number; completionTokens: number; costEur: number };
  status: "active" | "ended";
}

type CaseStatus = "abierto" | "aplicado" | "descartado";

interface SavedCase {
  id: string;
  name: string;
  locationName: string;
  channel: Channel;
  callerKind: CallerKind;
  turnsCount: number;
  createdAt: string;
  status: CaseStatus;
  resolution: string | null;
  resolvedAt: string | null;
  actionId: string | null;
}

interface CaseDetailTurn {
  role: "user" | "assistant";
  text: string;
  toolCalls: ToolCall[];
}

interface CaseDetail {
  case: SavedCase & { expectations: unknown };
  turns: CaseDetailTurn[];
}

interface ChatTurn {
  role: "user" | "assistant";
  text: string;
  toolCalls?: ToolCall[];
  time: string;
}

interface ActiveSession extends SessionStartResponse {
  business: BusinessItem;
  channel: Channel;
  callerKind: CallerKind;
  callerName: string | null;
  callerPhone: string;
}

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

function isValidE164(phone: string): boolean {
  return E164_REGEX.test(phone.trim());
}

function isDisabledError(err: unknown): boolean {
  const code = (err as { response?: { data?: { code?: string } } })?.response
    ?.data?.code;
  return code === "CARMEN_SIM_DISABLED";
}

function formatArgValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function nowLabel(): string {
  return new Date().toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function tabClasses(active: boolean): string {
  return `px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
    active
      ? "bg-primary-100 text-primary-700"
      : "text-gray-600 hover:bg-gray-100"
  }`;
}

function choiceButtonClasses(active: boolean): string {
  return `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
    active
      ? "bg-primary-600 text-white border-primary-600"
      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
  }`;
}

function BookingModeChip({ mode }: { mode: BookingMode }) {
  return mode === "tables" ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex-shrink-0">
      <Armchair className="w-3 h-3" /> Mesas
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 flex-shrink-0">
      <CalendarClock className="w-3 h-3" /> Citas
    </span>
  );
}

function ChannelIcon({
  channel,
  className,
}: {
  channel: Channel;
  className?: string;
}) {
  return channel === "voice" ? (
    <Phone className={className} />
  ) : (
    <MessageCircle className={className} />
  );
}

function channelLabel(channel: Channel): string {
  return channel === "voice" ? "Voz (llamada)" : "WhatsApp";
}

// ---------------------------------------------------------------------------
// Selector de negocio (buscable, filtro en el propio navegador)
// ---------------------------------------------------------------------------

function BusinessPicker({
  businesses,
  selected,
  onSelect,
  onClear,
}: {
  businesses: BusinessItem[];
  selected: BusinessItem | null;
  onSelect: (b: BusinessItem) => void;
  onClear: () => void;
}) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return businesses;
    return businesses.filter(
      (b) =>
        b.locationName.toLowerCase().includes(q) ||
        b.organizationName.toLowerCase().includes(q),
    );
  }, [businesses, term]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50">
        <div className="min-w-0 flex items-center gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {selected.locationName}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {selected.organizationName}
            </p>
          </div>
          <BookingModeChip mode={selected.bookingMode} />
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-primary-600 hover:underline flex-shrink-0"
        >
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Busca por negocio u organización..."
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {businesses.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">
              No hay negocios disponibles.
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">
              Ninguno coincide con la búsqueda.
            </p>
          ) : (
            filtered.slice(0, 30).map((b) => (
              <button
                key={b.locationId}
                type="button"
                onClick={() => {
                  onSelect(b);
                  setTerm("");
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900 truncate">
                    {b.locationName}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">
                    {b.organizationName}
                  </span>
                </span>
                <BookingModeChip mode={b.bookingMode} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selector de cliente conocido (buscador con debounce contra el negocio)
// ---------------------------------------------------------------------------

function CustomerPicker({
  locationId,
  selected,
  onSelect,
  onClear,
}: {
  locationId: string;
  selected: CustomerItem | null;
  onSelect: (c: CustomerItem) => void;
  onClear: () => void;
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<CustomerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      apiClient
        .get<{ success: boolean; data: CustomerItem[] }>(
          `/admin/carmen-sim/businesses/${locationId}/customers`,
          { params: { q: term.trim() } },
        )
        .then((res) => setResults(res.data.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [term, locationId]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {selected.firstName} {selected.lastName ?? ""}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {selected.phone ?? "Sin teléfono guardado"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-primary-600 hover:underline flex-shrink-0"
        >
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Busca por nombre o teléfono..."
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
      {open && term.trim().length >= 2 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center flex items-center justify-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando...
            </p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">
              Sin resultados.
            </p>
          ) : (
            results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onSelect(c);
                  setTerm("");
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50"
              >
                <span className="block text-sm font-medium text-gray-900 truncate">
                  {c.firstName} {c.lastName ?? ""}
                </span>
                <span className="block text-xs text-gray-500 truncate">
                  {c.phone ?? "Sin teléfono"}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bloque plegable por herramienta llamada — lo más importante de la pantalla
// ---------------------------------------------------------------------------

function ToolCallBlock({ tool }: { tool: ToolCall }) {
  const isReal = tool.mode === "real";
  const argEntries = Object.entries(tool.args);
  return (
    <details className="rounded-lg border border-gray-200 bg-gray-50 open:bg-white">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-700 flex items-center gap-2 flex-wrap">
        <Wrench className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="font-mono truncate">{tool.name}</span>
        <span
          className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
            isReal
              ? "bg-blue-100 text-blue-700"
              : "bg-amber-100 text-amber-700"
          }`}
        >
          {isReal ? "real" : "en seco"}
        </span>
      </summary>
      <div className="px-3 pb-3 pt-1 text-xs space-y-2 border-t border-gray-100">
        {!isReal && (
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Acción simulada: no se ha ejecutado.
          </p>
        )}
        <div>
          <p className="text-gray-500 font-medium mb-1">Argumentos</p>
          {argEntries.length === 0 ? (
            <p className="text-gray-400 italic">Sin argumentos</p>
          ) : (
            <ul className="space-y-0.5">
              {argEntries.map(([key, value]) => (
                <li key={key} className="font-mono text-gray-700 break-words">
                  <span className="text-gray-500">{key}:</span>{" "}
                  {formatArgValue(value)}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-gray-500 font-medium mb-1">Resultado</p>
          <pre className="whitespace-pre-wrap break-words bg-gray-900 text-gray-100 rounded p-2 font-mono text-[11px]">
            {tool.result}
          </pre>
        </div>
      </div>
    </details>
  );
}

function ChatBubble({ turn }: { turn: ChatTurn }) {
  const isUser = turn.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%] md:max-w-[70%] min-w-0">
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs font-medium text-gray-500">Carmen</span>
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">
              IA
            </span>
          </div>
        )}
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isUser
              ? "bg-primary-600 text-white rounded-tr-md"
              : "bg-white border border-gray-200 text-gray-800 rounded-tl-md"
          }`}
        >
          {turn.text}
        </div>
        <p
          className={`text-[11px] text-gray-400 mt-1 ${isUser ? "text-right" : ""}`}
        >
          {turn.time}
        </p>
        {!isUser && turn.toolCalls && turn.toolCalls.length > 0 && (
          <div className="mt-1.5 space-y-1.5">
            {turn.toolCalls.map((tc, i) => (
              <ToolCallBlock key={`${tc.name}-${i}`} tool={tc} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulario inline "Guardar como caso de prueba" (NO window.prompt)
// ---------------------------------------------------------------------------

function SaveCaseModal({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}) {
  const { showToast } = useGlobalToast();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await apiClient.post(`/admin/carmen-sim/sessions/${sessionId}/save-case`, {
        name: trimmed,
      });
      showToast("Caso de prueba guardado", "success");
      onClose();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo guardar el caso"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl max-w-sm w-full p-5 shadow-xl">
        <h3 className="text-base font-bold text-gray-900 mb-3">
          Guardar como caso de prueba
        </h3>
        <label className="block text-xs text-gray-500 mb-1">
          Nombre del caso
        </label>
        <input
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSave();
            }
          }}
          placeholder="Ej: Cliente pide mesa para 4 el sábado"
          maxLength={120}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !name.trim()}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estado de un caso guardado (abierto/aplicado/descartado)
// ---------------------------------------------------------------------------

function CaseStatusChip({ status }: { status: CaseStatus }) {
  const map: Record<CaseStatus, string> = {
    abierto: "bg-amber-100 text-amber-700",
    aplicado: "bg-emerald-100 text-emerald-700",
    descartado: "bg-gray-100 text-gray-600",
  };
  const labels: Record<CaseStatus, string> = {
    abierto: "Abierto",
    aplicado: "Aplicado",
    descartado: "Descartado",
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${map[status]}`}
    >
      {labels[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Drawer de detalle de un caso: conversación + tool calls + resolución
// ---------------------------------------------------------------------------

function CaseDetailModal({
  caseId,
  businesses,
  onClose,
  onResolved,
}: {
  caseId: string;
  businesses: BusinessItem[];
  onClose: () => void;
  onResolved: (updated: SavedCase) => void;
}) {
  const { showToast } = useGlobalToast();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolveMode, setResolveMode] = useState<
    "aplicado" | "descartado" | null
  >(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: CaseDetail;
      }>(`/admin/carmen-sim/cases/${caseId}`);
      setDetail(data.data);
    } catch (err) {
      setError(extractApiErrorMessage(err, "No se pudo cargar el caso"));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleResolve = async () => {
    if (!resolveMode || saving) return;
    setSaving(true);
    try {
      const { data } = await apiClient.patch<{
        success: boolean;
        data: SavedCase;
      }>(`/admin/carmen-sim/cases/${caseId}`, {
        status: resolveMode,
        ...(note.trim() ? { resolution: note.trim() } : {}),
      });
      showToast(
        resolveMode === "aplicado"
          ? "Caso marcado como aplicado"
          : "Caso descartado",
        "success",
      );
      onResolved(data.data);
      onClose();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo actualizar el caso"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const askBrain = () => {
    if (!detail) return;
    const business = businesses.find(
      (b) => b.locationName === detail.case.locationName,
    );
    const params = new URLSearchParams({ case: caseId });
    if (business) params.set("locationId", business.locationId);
    navigate(`/admin/carmen-brain?${params.toString()}`);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[88dvh] flex flex-col shadow-xl">
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {detail?.case.name ?? "Caso de prueba"}
            </h3>
            {detail && (
              <p className="text-xs text-gray-500 truncate">
                {detail.case.locationName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-6">{error}</p>
          ) : detail ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <CaseStatusChip status={detail.case.status} />
                {detail.case.resolvedAt && (
                  <span className="text-xs text-gray-400">
                    Resuelto el{" "}
                    {format(new Date(detail.case.resolvedAt), "d MMM, HH:mm", {
                      locale: es,
                    })}
                  </span>
                )}
              </div>
              {detail.case.resolution && (
                <p className="text-xs text-gray-500 italic bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                  {detail.case.resolution}
                </p>
              )}
              <div className="space-y-3">
                {detail.turns.map((turn, i) => (
                  <div
                    key={i}
                    className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div className="max-w-[90%] space-y-1.5">
                      <div
                        className={`px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                          turn.role === "user"
                            ? "bg-primary-600 text-white rounded-tr-md"
                            : "bg-gray-50 border border-gray-200 text-gray-800 rounded-tl-md"
                        }`}
                      >
                        {turn.text}
                      </div>
                      {turn.role === "assistant" && turn.toolCalls.length > 0 && (
                        <div className="space-y-1.5">
                          {turn.toolCalls.map((tc, j) => (
                            <ToolCallBlock key={j} tool={tc} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {detail && (
          <div className="flex-shrink-0 border-t border-gray-100 p-3 space-y-2">
            {resolveMode ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-600">
                  {resolveMode === "aplicado"
                    ? "¿Qué se aplicó? (opcional)"
                    : "¿Por qué se descarta? (opcional)"}
                </p>
                <input
                  type="text"
                  autoFocus
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={300}
                  placeholder="Escribe una nota..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setResolveMode(null);
                      setNote("");
                    }}
                    disabled={saving}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => void handleResolve()}
                    disabled={saving}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Confirmar"
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={askBrain}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50"
                >
                  <Brain className="w-4 h-4" /> Preguntar al cerebro sobre
                  este caso
                </button>
                {detail.case.status !== "aplicado" && (
                  <button
                    onClick={() => setResolveMode("aplicado")}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-medium hover:bg-emerald-50"
                  >
                    <CheckCircle className="w-4 h-4" /> Marcar como aplicado
                  </button>
                )}
                {detail.case.status !== "descartado" && (
                  <button
                    onClick={() => setResolveMode("descartado")}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50"
                  >
                    <XCircle className="w-4 h-4" /> Descartar
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña "Casos guardados"
// ---------------------------------------------------------------------------

function CasesTab({ businesses }: { businesses: BusinessItem[] }) {
  const [cases, setCases] = useState<SavedCase[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCaseId, setOpenCaseId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: SavedCase[];
      }>("/admin/carmen-sim/cases");
      setCases(data.data);
    } catch (err) {
      setError(
        extractApiErrorMessage(err, "No se pudieron cargar los casos guardados"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleResolved = (updated: SavedCase) => {
    setCases((prev) =>
      prev ? prev.map((c) => (c.id === updated.id ? updated : c)) : prev,
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Casos guardados
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Conversaciones de prueba guardadas. Haz clic en una para ver la
            conversación, resolverla o preguntarle al cerebro por ella.
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {loading && !cases ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="px-4 py-6 text-center text-sm text-red-600">
          {error}
        </div>
      ) : !cases || cases.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-gray-400">
          Todavía no hay casos guardados.
        </p>
      ) : (
        <div>
          {cases.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpenCaseId(c.id)}
              className="w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {c.name}
                  </p>
                  <CaseStatusChip status={c.status} />
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {c.locationName}
                  {c.resolvedAt &&
                    ` · resuelto el ${format(new Date(c.resolvedAt), "d MMM, HH:mm", { locale: es })}`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  <ChannelIcon channel={c.channel} className="w-3 h-3" />
                  {c.channel === "voice" ? "Voz" : "WhatsApp"}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  {c.callerKind === "known" ? "Cliente conocido" : "Desconocido"}
                </span>
                <span className="text-xs text-gray-400">
                  {c.turnsCount} mensaje{c.turnsCount === 1 ? "" : "s"}
                </span>
                <span className="text-xs text-gray-400">
                  {format(new Date(c.createdAt), "d MMM, HH:mm", { locale: es })}
                </span>
                {c.actionId && (
                  <Link
                    to={`/admin/carmen-brain?action=${c.actionId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
                  >
                    Ver acción <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {openCaseId && (
        <CaseDetailModal
          caseId={openCaseId}
          businesses={businesses}
          onClose={() => setOpenCaseId(null)}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function CarmenSim() {
  const { showToast } = useGlobalToast();
  const [tab, setTab] = useState<"simulador" | "casos">("simulador");
  const [stage, setStage] = useState<"setup" | "chat">("setup");

  // Negocios
  const [businesses, setBusinesses] = useState<BusinessItem[]>([]);
  const [businessesState, setBusinessesState] = useState<
    "loading" | "ready" | "error" | "disabled"
  >("loading");
  const [businessesErrorMsg, setBusinessesErrorMsg] = useState<string | null>(
    null,
  );

  const loadBusinesses = useCallback(async () => {
    setBusinessesState("loading");
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: BusinessItem[];
      }>("/admin/carmen-sim/businesses");
      setBusinesses(data.data);
      setBusinessesState("ready");
    } catch (err) {
      if (isDisabledError(err)) {
        setBusinessesState("disabled");
      } else {
        setBusinessesErrorMsg(
          extractApiErrorMessage(err, "No se pudieron cargar los negocios"),
        );
        setBusinessesState("error");
      }
    }
  }, []);

  useEffect(() => {
    void loadBusinesses();
  }, [loadBusinesses]);

  // Formulario de arranque
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessItem | null>(
    null,
  );
  const [channel, setChannel] = useState<Channel | null>(null);
  const [callerKind, setCallerKind] = useState<CallerKind>("unknown");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerItem | null>(
    null,
  );
  const [callerPhone, setCallerPhone] = useState("+34600000001");
  const [starting, setStarting] = useState(false);

  const handleSelectBusiness = (b: BusinessItem) => {
    setSelectedBusiness(b);
    setChannel(null);
    setCallerKind("unknown");
    setSelectedCustomer(null);
    setCallerPhone("+34600000001");
  };

  const handleClearBusiness = () => {
    setSelectedBusiness(null);
    setChannel(null);
  };

  const handleSetCallerKind = (kind: CallerKind) => {
    setCallerKind(kind);
    setSelectedCustomer(null);
    setCallerPhone(kind === "unknown" ? "+34600000001" : "");
  };

  const handleSelectCustomer = (c: CustomerItem) => {
    setSelectedCustomer(c);
    setCallerPhone(c.phone ?? "");
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCallerPhone("");
  };

  const canStart = Boolean(
    selectedBusiness &&
      channel &&
      isValidE164(callerPhone) &&
      (callerKind === "unknown" || selectedCustomer),
  );

  // Sesión activa / chat
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [costTotal, setCostTotal] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns.length, sending]);

  const handleStart = async () => {
    if (!selectedBusiness || !channel || !canStart || starting) return;
    setStarting(true);
    try {
      const body: {
        locationId: string;
        channel: Channel;
        callerKind: CallerKind;
        customerId?: string;
        callerPhone: string;
      } = {
        locationId: selectedBusiness.locationId,
        channel,
        callerKind,
        callerPhone: callerPhone.trim(),
      };
      if (callerKind === "known" && selectedCustomer) {
        body.customerId = selectedCustomer.id;
      }
      const { data } = await apiClient.post<{
        success: boolean;
        data: SessionStartResponse;
      }>("/admin/carmen-sim/sessions", body);
      const resp = data.data;
      setSession({
        ...resp,
        business: selectedBusiness,
        channel,
        callerKind,
        callerName: selectedCustomer
          ? `${selectedCustomer.firstName} ${selectedCustomer.lastName ?? ""}`.trim()
          : null,
        callerPhone: callerPhone.trim(),
      });
      setTurns(
        resp.saludo
          ? [{ role: "assistant", text: resp.saludo, time: nowLabel() }]
          : [],
      );
      setCostTotal(0);
      setSessionEnded(false);
      setInputText("");
      setStage("chat");
    } catch (err) {
      if (isDisabledError(err)) {
        showToast("El simulador de Carmen está apagado ahora mismo.", "info");
      } else {
        showToast(
          extractApiErrorMessage(err, "No se pudo iniciar la conversación"),
          "error",
        );
      }
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending || !session || sessionEnded) return;
    setInputText("");
    setSending(true);
    setTurns((prev) => [...prev, { role: "user", text, time: nowLabel() }]);
    try {
      const { data } = await apiClient.post<{
        success: boolean;
        data: MessageResponse;
      }>(`/admin/carmen-sim/sessions/${session.sessionId}/messages`, { text });
      const resp = data.data;
      setTurns((prev) => [
        ...prev,
        {
          role: "assistant",
          text: resp.reply,
          toolCalls: resp.toolCalls,
          time: nowLabel(),
        },
      ]);
      setCostTotal((c) => c + resp.usage.costEur);
      if (resp.status === "ended") setSessionEnded(true);
    } catch (err) {
      // Quita el mensaje optimista y devuelve el texto al input.
      setTurns((prev) => prev.slice(0, -1));
      setInputText(text);
      if (isDisabledError(err)) {
        showToast("El simulador de Carmen se ha apagado.", "info");
      } else {
        showToast(
          extractApiErrorMessage(err, "No se pudo enviar el mensaje"),
          "error",
        );
      }
    } finally {
      setSending(false);
    }
  };

  const handleComposerSubmit = (e: FormEvent) => {
    e.preventDefault();
    void handleSend();
  };

  const handleNewConversation = () => {
    setStage("setup");
    setSession(null);
    setTurns([]);
    setCostTotal(0);
    setSessionEnded(false);
    setInputText("");
  };

  // ── Pantalla de chat a página completa ──
  if (stage === "chat" && session) {
    return (
      <div className="flex-1 min-h-0 flex flex-col bg-gray-50 overflow-hidden -m-4 lg:-m-8">
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 md:px-4 py-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              onClick={handleNewConversation}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Nueva conversación
            </button>
            <button
              onClick={() => setSaveModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 flex-shrink-0"
            >
              <Save className="w-3.5 h-3.5" /> Guardar como caso de prueba
            </button>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base md:text-lg font-bold text-gray-900 truncate">
                {session.business.locationName}
              </h1>
              <BookingModeChip mode={session.business.bookingMode} />
            </div>
            <p className="text-xs text-gray-500 truncate">
              {session.business.organizationName}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-600">
            <span className="inline-flex items-center gap-1">
              <ChannelIcon channel={session.channel} className="w-3.5 h-3.5" />
              {channelLabel(session.channel)}
            </span>
            <span>·</span>
            <span>
              {session.callerKind === "known"
                ? `Cliente conocido: ${session.callerName ?? session.callerPhone}`
                : `Desconocido: ${session.callerPhone}`}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-gray-500">
            <span>
              Prompt de {session.promptChars.toLocaleString("es-ES")} caracteres
              (~{session.tokensAprox.toLocaleString("es-ES")} tokens)
            </span>
            <span className="font-semibold text-gray-900">
              {costTotal.toFixed(2)} €
            </span>
          </div>
          {session.herramientas.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {session.herramientas.map((h) => (
                <span
                  key={h}
                  className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                >
                  {h}
                </span>
              ))}
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3"
        >
          {turns.map((turn, i) => (
            <ChatBubble key={i} turn={turn} />
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-gray-200 text-xs text-gray-500 rounded-tl-md">
                Carmen está escribiendo…
              </div>
            </div>
          )}
        </div>

        {sessionEnded ? (
          <div className="flex-shrink-0 bg-gray-100 border-t border-gray-200 p-3 text-center text-sm text-gray-500">
            Carmen terminó la conversación. Puedes guardar el caso o empezar
            una nueva.
          </div>
        ) : (
          <ConversationComposer
            value={inputText}
            onChange={setInputText}
            onSubmit={handleComposerSubmit}
            sending={sending}
            placeholder="Escribe como si fueras el cliente... (Intro para enviar)"
          />
        )}

        {saveModalOpen && (
          <SaveCaseModal
            sessionId={session.sessionId}
            onClose={() => setSaveModalOpen(false)}
          />
        )}
      </div>
    );
  }

  // ── Arranque + casos guardados ──
  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FlaskConical className="w-6 h-6 text-primary-600" /> Probar a Carmen
        </h1>
        <p className="text-gray-500 text-sm mt-1 max-w-2xl">
          Chatea por texto con el cerebro de Carmen de un negocio como si
          fueras un cliente. Todo va en seco: ninguna acción (reservar,
          cancelar, enviar WhatsApp...) se ejecuta de verdad.
        </p>
      </div>

      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border border-gray-200 w-fit">
        <button onClick={() => setTab("simulador")} className={tabClasses(tab === "simulador")}>
          Simulador
        </button>
        <button onClick={() => setTab("casos")} className={tabClasses(tab === "casos")}>
          Casos guardados
        </button>
      </div>

      {tab === "casos" ? (
        <CasesTab businesses={businesses} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 max-w-2xl mx-auto space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-1.5">
              1. Elige el negocio
            </h2>
            {businessesState === "loading" ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando
                negocios...
              </div>
            ) : businessesState === "disabled" ? (
              <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  El simulador de Carmen está apagado ahora mismo. Actívalo
                  para poder probarlo.
                </span>
              </div>
            ) : businessesState === "error" ? (
              <div className="flex items-center gap-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{businessesErrorMsg}</span>
                <button
                  onClick={() => void loadBusinesses()}
                  className="underline flex-shrink-0"
                >
                  Reintentar
                </button>
              </div>
            ) : (
              <BusinessPicker
                businesses={businesses}
                selected={selectedBusiness}
                onSelect={handleSelectBusiness}
                onClear={handleClearBusiness}
              />
            )}
          </div>

          {selectedBusiness && (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-1.5">
                2. Canal
              </h2>
              {!selectedBusiness.channels.voice &&
              !selectedBusiness.channels.whatsapp ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  Este negocio no tiene IA contratada.
                </p>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {selectedBusiness.channels.whatsapp && (
                    <button
                      type="button"
                      onClick={() => setChannel("whatsapp")}
                      className={choiceButtonClasses(channel === "whatsapp")}
                    >
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </button>
                  )}
                  {selectedBusiness.channels.voice && (
                    <button
                      type="button"
                      onClick={() => setChannel("voice")}
                      className={choiceButtonClasses(channel === "voice")}
                    >
                      <Phone className="w-4 h-4" /> Voz (llamada)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {selectedBusiness && channel && (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-1.5">
                3. ¿Quién eres?
              </h2>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => handleSetCallerKind("known")}
                  className={choiceButtonClasses(callerKind === "known")}
                >
                  Cliente conocido
                </button>
                <button
                  type="button"
                  onClick={() => handleSetCallerKind("unknown")}
                  className={choiceButtonClasses(callerKind === "unknown")}
                >
                  Desconocido
                </button>
              </div>

              {callerKind === "known" ? (
                <div className="space-y-2">
                  <CustomerPicker
                    locationId={selectedBusiness.locationId}
                    selected={selectedCustomer}
                    onSelect={handleSelectCustomer}
                    onClear={handleClearCustomer}
                  />
                  {selectedCustomer && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Teléfono a usar
                      </label>
                      <input
                        type="tel"
                        value={callerPhone}
                        onChange={(e) => setCallerPhone(e.target.value)}
                        placeholder="+34600000001"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      {!selectedCustomer.phone && (
                        <p className="text-xs text-amber-600 mt-1">
                          Este cliente no tiene teléfono guardado: escríbelo a
                          mano.
                        </p>
                      )}
                      {callerPhone && !isValidE164(callerPhone) && (
                        <p className="text-xs text-red-600 mt-1">
                          Formato inválido: usa +[prefijo][número], ej.
                          +34600000001.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Teléfono (formato internacional)
                  </label>
                  <input
                    type="tel"
                    value={callerPhone}
                    onChange={(e) => setCallerPhone(e.target.value)}
                    placeholder="+34600000001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {callerPhone && !isValidE164(callerPhone) && (
                    <p className="text-xs text-red-600 mt-1">
                      Formato inválido: usa +[prefijo][número], ej.
                      +34600000001.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={!canStart || starting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {starting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Empezar conversación
          </button>
        </div>
      )}
    </div>
  );
}
